import { useQueryClient } from '@tanstack/react-query';
import { createContext, type ReactNode, useContext, useEffect, useState } from 'react';

interface DemoModeContextType {
  isDemoMode: boolean;
  setDemoMode: (value: boolean) => void;
  dataSource: 'demo' | 'live';
  setDataSource: (source: 'demo' | 'live') => void;
}

const DemoModeContext = createContext<DemoModeContextType | undefined>(undefined);

const DEMO_MODE_KEY = 'venue-intel-demo-mode';

export function DemoModeProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();

  const [isDemoMode, setIsDemoMode] = useState(() => {
    const stored = localStorage.getItem(DEMO_MODE_KEY);
    return stored === null ? true : stored === 'true';
  });

  // Track the actual data source (may differ from demo mode if API fails)
  const [dataSource, setDataSource] = useState<'demo' | 'live'>('demo');

  useEffect(() => {
    localStorage.setItem(DEMO_MODE_KEY, String(isDemoMode));
  }, [isDemoMode]);

  const setDemoMode = (value: boolean) => {
    setIsDemoMode(value);
    if (value) {
      setDataSource('demo');
    }
    // Flush all cached data — services will re-fetch from the correct source
    queryClient.invalidateQueries();
  };

  return (
    <DemoModeContext.Provider value={{ isDemoMode, setDemoMode, dataSource, setDataSource }}>
      {children}
    </DemoModeContext.Provider>
  );
}

export function useDemoMode() {
  const context = useContext(DemoModeContext);
  if (context === undefined) {
    throw new Error('useDemoMode must be used within a DemoModeProvider');
  }
  return context;
}
